import { useState, useEffect, useRef } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Facebook, Send, Link2, Mail, Plus, Eye, Calendar as CalendarIcon, Trash2, Edit, Users, Search, Pencil, FileText, Upload, FileDown } from "lucide-react";
import '@/lib/tinymce-imports';
import { Editor } from "@tinymce/tinymce-react";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { format } from "date-fns";

interface NewsletterTemplate {
  id: string;
  pta_id: string | null;
  name: string;
  subject: string;
  html_content: string;
  is_system_template: boolean;
  created_at: string;
  updated_at: string;
}

export default function CommunicationsDashboard() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [ptaId, setPtaId] = useState<string | null>(null);
  const [ptaLogoUrl, setPtaLogoUrl] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("newsletter");
  const [newsletterTab, setNewsletterTab] = useState("compose");

  // Compose state
  const [subject, setSubject] = useState("");
  const [editingId, setEditingId] = useState<string | null>(null);
  const editorRef = useRef<any>(null);
  const ptaIdRef = useRef<string | null>(null);
  const [editorContent, setEditorContent] = useState("");

  // Schedule
  const [scheduleDate, setScheduleDate] = useState<Date | undefined>();
  const [scheduleTime, setScheduleTime] = useState("09:00");

  // Data
  const [newsletters, setNewsletters] = useState<any[]>([]);
  const [subscribers, setSubscribers] = useState<any[]>([]);
  const [subscriberSearch, setSubscriberSearch] = useState("");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState("");
  const [previewSubject, setPreviewSubject] = useState("");
  const [sending, setSending] = useState(false);

  // PDF send state
  const [pdfMode, setPdfMode] = useState<'none' | 'upload' | 'generate'>('none');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [pdfSubject, setPdfSubject] = useState("");
  const [sendingPdf, setSendingPdf] = useState(false);

  // Templates state
  const [templates, setTemplates] = useState<NewsletterTemplate[]>([]);
  const [templateDialogOpen, setTemplateDialogOpen] = useState(false);
  const [editTemplateId, setEditTemplateId] = useState<string | null>(null);
  const [templateName, setTemplateName] = useState("");
  const [templateSubject, setTemplateSubject] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [templatePreview, setTemplatePreview] = useState<NewsletterTemplate | null>(null);

  useEffect(() => {
    if (user) fetchPtaId();
  }, [user]);

  useEffect(() => {
    ptaIdRef.current = ptaId;
    if (ptaId) {
      fetchNewsletters();
      fetchSubscribers();
      fetchTemplates();
    }
  }, [ptaId]);

  const injectPtaLogo = (html: string, logoUrl: string | null): string => {
    if (!logoUrl) return html;
    const logoRow = `<tr><td style="padding:20px 30px 10px 30px;"><img src="${logoUrl}" alt="PTA Logo" style="max-height:50px;width:auto;display:block;" /></td></tr>`;
    // Inject after the SECOND <tbody> tag (inner white content table)
    const firstTbodyIndex = html.indexOf('<tbody');
    if (firstTbodyIndex === -1) return html;
    const secondTbodyIndex = html.indexOf('<tbody', firstTbodyIndex + 1);
    const tbodyIndex = secondTbodyIndex !== -1 ? secondTbodyIndex : firstTbodyIndex;
    const closingBracket = html.indexOf('>', tbodyIndex);
    if (closingBracket === -1) return html;
    return html.slice(0, closingBracket + 1) + logoRow + html.slice(closingBracket + 1);
  };

  const fetchPtaId = async () => {
    const { data } = await supabase
      .from('members')
      .select('pta_id')
      .eq('user_id', user?.id)
      .maybeSingle();
    if (data?.pta_id) {
      setPtaId(data.pta_id);
      // Fetch PTA logo
      const { data: ptaData } = await supabase
        .from('ptas')
        .select('logo_url')
        .eq('id', data.pta_id)
        .maybeSingle();
      setPtaLogoUrl(ptaData?.logo_url || null);
    }
  };

  const fetchNewsletters = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('newsletters')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false });
    if (data) setNewsletters(data);
  };

  const fetchSubscribers = async () => {
    if (!ptaId) return;
    const { data } = await supabase
      .from('newsletter_subscribers')
      .select('*')
      .eq('pta_id', ptaId)
      .order('created_at', { ascending: false });
    if (data) setSubscribers(data);
  };

  const fetchTemplates = async () => {
    const { data } = await supabase
      .from('newsletter_templates')
      .select('*')
      .order('is_system_template', { ascending: false })
      .order('name');
    if (data) setTemplates(data as NewsletterTemplate[]);
  };

  const handleSaveDraft = async () => {
    if (!ptaId || !subject.trim()) {
      toast({ title: "Subject required", variant: "destructive" });
      return;
    }

    const htmlContent = editorContent;
    
    if (editingId) {
      const { error } = await supabase
        .from('newsletters')
        .update({ subject, html_content: htmlContent, status: 'draft' })
        .eq('id', editingId);
      if (error) {
        toast({ title: "Error saving", description: error.message, variant: "destructive" });
        return;
      }
    } else {
      const { error } = await supabase
        .from('newsletters')
        .insert({ pta_id: ptaId, subject, html_content: htmlContent, status: 'draft', created_by: user?.id });
      if (error) {
        toast({ title: "Error saving", description: error.message, variant: "destructive" });
        return;
      }
    }

    toast({ title: "Draft saved" });
    resetCompose();
    fetchNewsletters();
    setNewsletterTab("drafts");
  };

  const handleSchedule = async () => {
    if (!ptaId || !subject.trim() || !scheduleDate) {
      toast({ title: "Subject and date required", variant: "destructive" });
      return;
    }

    const htmlContent = editorContent;
    const [hours, minutes] = scheduleTime.split(':').map(Number);
    const scheduledAt = new Date(scheduleDate);
    scheduledAt.setHours(hours, minutes, 0, 0);

    if (editingId) {
      await supabase
        .from('newsletters')
        .update({ subject, html_content: htmlContent, status: 'scheduled', scheduled_at: scheduledAt.toISOString() })
        .eq('id', editingId);
    } else {
      await supabase
        .from('newsletters')
        .insert({ pta_id: ptaId, subject, html_content: htmlContent, status: 'scheduled', scheduled_at: scheduledAt.toISOString(), created_by: user?.id });
    }

    toast({ title: "Newsletter scheduled", description: `Will be sent on ${format(scheduledAt, 'PPP')} at ${scheduleTime}` });
    resetCompose();
    fetchNewsletters();
    setNewsletterTab("drafts");
  };

  const handleSendNow = async () => {
    if (!ptaId || !subject.trim()) {
      toast({ title: "Subject required", variant: "destructive" });
      return;
    }

    setSending(true);
    const htmlContent = editorContent;

    try {
      let newsletterId = editingId;
      if (editingId) {
        await supabase
          .from('newsletters')
          .update({ subject, html_content: htmlContent, status: 'sending' })
          .eq('id', editingId);
      } else {
        const { data, error } = await supabase
          .from('newsletters')
          .insert({ pta_id: ptaId, subject, html_content: htmlContent, status: 'sending', created_by: user?.id })
          .select('id')
          .single();
        if (error) throw error;
        newsletterId = data.id;
      }

      const { error } = await supabase.functions.invoke('send-newsletter', {
        body: { newsletterId }
      });

      if (error) throw error;

      toast({ title: "Newsletter sent!", description: "Your newsletter is being delivered to subscribers." });
      resetCompose();
      fetchNewsletters();
      setNewsletterTab("sent");
    } catch (err: any) {
      toast({ title: "Error sending", description: err.message, variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  const handleEdit = (newsletter: any) => {
    setEditingId(newsletter.id);
    setSubject(newsletter.subject);
    setEditorContent(newsletter.html_content || '');
    if (newsletter.scheduled_at) {
      setScheduleDate(new Date(newsletter.scheduled_at));
      setScheduleTime(format(new Date(newsletter.scheduled_at), 'HH:mm'));
    }
    setNewsletterTab("compose");
  };

  const handleDelete = async (id: string) => {
    await supabase.from('newsletters').delete().eq('id', id);
    toast({ title: "Newsletter deleted" });
    fetchNewsletters();
  };

  const handleCancelSchedule = async (id: string) => {
    await supabase
      .from('newsletters')
      .update({ status: 'draft', scheduled_at: null })
      .eq('id', id);
    toast({ title: "Schedule cancelled" });
    fetchNewsletters();
  };

  const handleRemoveSubscriber = async (id: string) => {
    await supabase.from('newsletter_subscribers').delete().eq('id', id);
    toast({ title: "Subscriber removed" });
    fetchSubscribers();
  };

  const resetCompose = () => {
    setSubject("");
    setEditingId(null);
    setScheduleDate(undefined);
    setScheduleTime("09:00");
    setEditorContent('');
  };

  const handleSendPdf = async (sendMode: 'upload' | 'generate') => {
    if (!ptaId) return;
    
    if (sendMode === 'upload') {
      if (!pdfFile || !pdfSubject.trim()) {
        toast({ title: "PDF file and subject required", variant: "destructive" });
        return;
      }
      setSendingPdf(true);
      try {
        const reader = new FileReader();
        const base64 = await new Promise<string>((resolve, reject) => {
          reader.onload = () => {
            const result = reader.result as string;
            resolve(result.split(',')[1]); // strip data:...;base64, prefix
          };
          reader.onerror = reject;
          reader.readAsDataURL(pdfFile);
        });

        const { error } = await supabase.functions.invoke('send-newsletter-pdf', {
          body: { ptaId, subject: pdfSubject, pdfBase64: base64, mode: 'upload' }
        });
        if (error) throw error;

        toast({ title: "PDF newsletter sent!", description: "Email with download link is being delivered." });
        setPdfFile(null);
        setPdfSubject("");
        setPdfMode('none');
        fetchNewsletters();
        setNewsletterTab("sent");
      } catch (err: any) {
        toast({ title: "Error sending PDF", description: err.message, variant: "destructive" });
      } finally {
        setSendingPdf(false);
      }
    } else if (sendMode === 'generate') {
      if (!subject.trim() || !editorContent.trim()) {
        toast({ title: "Subject and content required", variant: "destructive" });
        return;
      }
      setSendingPdf(true);
      try {
        // First save as newsletter
        let newsletterId = editingId;
        if (!newsletterId) {
          const { data, error } = await supabase
            .from('newsletters')
            .insert({ pta_id: ptaId, subject, html_content: editorContent, status: 'sending', created_by: user?.id })
            .select('id')
            .single();
          if (error) throw error;
          newsletterId = data.id;
        } else {
          await supabase
            .from('newsletters')
            .update({ subject, html_content: editorContent, status: 'sending' })
            .eq('id', newsletterId);
        }

        const { error } = await supabase.functions.invoke('send-newsletter-pdf', {
          body: { ptaId, subject, newsletterId, mode: 'generate' }
        });
        if (error) throw error;

        toast({ title: "PDF newsletter sent!", description: "Generated PDF and email with download link is being delivered." });
        resetCompose();
        fetchNewsletters();
        setNewsletterTab("sent");
      } catch (err: any) {
        toast({ title: "Error sending PDF", description: err.message, variant: "destructive" });
      } finally {
        setSendingPdf(false);
      }
    }
  };

  const handlePreview = (newsletter?: any) => {
    setPreviewSubject(newsletter?.subject || subject);
    setPreviewContent(newsletter?.html_content || editorContent);
    setPreviewOpen(true);
  };

  // Template actions
  const handleUseTemplate = (template: NewsletterTemplate) => {
    setSubject(template.subject);
    const content = template.is_system_template ? injectPtaLogo(template.html_content, ptaLogoUrl) : template.html_content;
    setEditorContent(content);
    setEditingId(null);
    setNewsletterTab("compose");
    toast({ title: "Template loaded", description: `"${template.name}" loaded into composer` });
  };

  const handleSaveAsTemplate = async () => {
    if (!ptaId || !subject.trim() || !editorContent.trim()) return;
    const { error } = await supabase
      .from('newsletter_templates')
      .insert({ pta_id: ptaId, name: subject, subject, html_content: editorContent, is_system_template: false });
    if (error) {
      toast({ title: "Error saving template", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Template saved", description: `"${subject}" saved to your templates` });
    fetchTemplates();
  };

  const handleEditTemplate = (template: NewsletterTemplate) => {
    setEditTemplateId(template.id);
    setTemplateName(template.name);
    setTemplateSubject(template.subject);
    setTemplateContent(template.html_content);
    setTemplateDialogOpen(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateName.trim()) {
      toast({ title: "Template name required", variant: "destructive" });
      return;
    }
    if (!ptaId) return;

    if (editTemplateId) {
      const { error } = await supabase
        .from('newsletter_templates')
        .update({ name: templateName, subject: templateSubject, html_content: templateContent, updated_at: new Date().toISOString() })
        .eq('id', editTemplateId);
      if (error) {
        toast({ title: "Error saving template", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Template updated" });
    } else {
      const { error } = await supabase
        .from('newsletter_templates')
        .insert({ pta_id: ptaId, name: templateName, subject: templateSubject, html_content: templateContent, is_system_template: false });
      if (error) {
        toast({ title: "Error creating template", description: error.message, variant: "destructive" });
        return;
      }
      toast({ title: "Template created" });
    }

    setTemplateDialogOpen(false);
    fetchTemplates();
  };

  const handleDeleteTemplate = async (id: string) => {
    await supabase.from('newsletter_templates').delete().eq('id', id);
    toast({ title: "Template deleted" });
    fetchTemplates();
  };

  const draftNewsletters = newsletters.filter(n => n.status === 'draft' || n.status === 'scheduled');
  const sentNewsletters = newsletters.filter(n => n.status === 'sent' || n.status === 'failed');
  const activeSubscribers = subscribers.filter(s => s.is_subscribed);
  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(subscriberSearch.toLowerCase())
  );

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-semibold text-foreground">Communications Dashboard</h1>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="newsletter">Newsletter</TabsTrigger>
          {false && <TabsTrigger value="social-media">Social Media</TabsTrigger>}
        </TabsList>

        <TabsContent value="newsletter" className="space-y-4">

          <Tabs value={newsletterTab} onValueChange={setNewsletterTab}>
            <TabsList>
              <TabsTrigger value="compose">Compose</TabsTrigger>
              <TabsTrigger value="send-pdf">Send PDF</TabsTrigger>
              <TabsTrigger value="templates">Templates</TabsTrigger>
              <TabsTrigger value="drafts">Drafts & Scheduled ({draftNewsletters.length})</TabsTrigger>
              <TabsTrigger value="sent">Sent ({sentNewsletters.length})</TabsTrigger>
              <TabsTrigger value="subscribers">Subscribers ({subscribers.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="compose" className="space-y-4">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>{editingId ? 'Edit Newsletter' : 'Compose Newsletter'}</CardTitle>
                  <CardDescription>Create a newsletter to send to your subscribers</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="subject">Subject Line</Label>
                    <Input
                      id="subject"
                      placeholder="e.g. Summer Fair Update"
                      value={subject}
                      onChange={e => setSubject(e.target.value)}
                    />
                  </div>

                  {/* Rich Text Editor */}
                  <div className="space-y-2">
                    <Label>Content</Label>
                    <Editor
                      onInit={(_evt, editor) => { editorRef.current = editor; }}
                      value={editorContent}
                      onEditorChange={(content) => setEditorContent(content)}
                      licenseKey="gpl"
                      init={{
                        height: 400,
                        menubar: 'edit insert view format table tools',
                        plugins: 'advlist autolink lists link image table code fullscreen searchreplace charmap preview wordcount emoticons media',
                        toolbar: 'undo redo | blocks fontfamily fontsize | bold italic underline strikethrough | forecolor backcolor | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | blockquote hr | link image media table | emoticons charmap | code fullscreen | removeformat',
                        skin: false,
                        content_css: false,
                        branding: false,
                        promotion: false,
                        object_resizing: 'img,table',
                        image_advtab: true,
                        image_dimensions: true,
                        content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; padding: 8px; } img { max-width: 100%; height: auto; cursor: pointer; } img:hover { outline: 2px solid #2980b9; }',
                        automatic_uploads: true,
                        images_upload_handler: async (blobInfo: any) => {
                          const currentPtaId = ptaIdRef.current;
                          if (!currentPtaId) throw new Error('PTA not found');
                          const file = blobInfo.blob();
                          const allowedTypes = ['image/png', 'image/jpeg'];
                          if (!allowedTypes.includes(file.type)) throw new Error('Only PNG and JPG images are allowed');
                          if (file.size > 10 * 1024 * 1024) throw new Error('Max file size is 10MB');
                          const fileName = `${Date.now()}-${blobInfo.filename().replace(/[^a-zA-Z0-9.-]/g, '_')}`;
                          const filePath = `${currentPtaId}/newsletter/${fileName}`;
                          const { data, error } = await supabase.storage.from('shop-images').upload(filePath, file);
                          if (error) throw new Error(error.message);
                          const { data: { publicUrl } } = supabase.storage.from('shop-images').getPublicUrl(data.path);
                          return publicUrl;
                        },
                        file_picker_types: 'image',
                        file_picker_callback: (cb: any, _value: any, meta: any) => {
                          if (meta.filetype === 'image') {
                            const input = document.createElement('input');
                            input.setAttribute('type', 'file');
                            input.setAttribute('accept', 'image/png,image/jpeg');
                            input.addEventListener('change', () => {
                              const file = input.files?.[0];
                              if (file) cb(URL.createObjectURL(file), { title: file.name });
                            });
                            input.click();
                          }
                        },
                      }}
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 items-center justify-between pt-2">
                    <div className="flex gap-2 items-center flex-wrap">
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline">
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {scheduleDate ? format(scheduleDate, 'PPP') : 'Schedule'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={scheduleDate}
                            onSelect={setScheduleDate}
                            disabled={(date) => date < new Date()}
                            initialFocus
                            className="p-3 pointer-events-auto"
                          />
                        </PopoverContent>
                      </Popover>
                      {scheduleDate && (
                        <>
                          <Input
                            type="time"
                            value={scheduleTime}
                            onChange={e => setScheduleTime(e.target.value)}
                            className="w-[130px]"
                          />
                          <Button variant="ghost" size="sm" onClick={() => setScheduleDate(undefined)}>
                            Clear
                          </Button>
                        </>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {editingId && (
                        <Button variant="ghost" onClick={resetCompose}>Cancel Edit</Button>
                      )}
                      <Button variant="outline" onClick={handleSaveAsTemplate} disabled={!subject.trim() || !editorContent.trim()}>
                        <FileText className="h-4 w-4 mr-2" />
                        Save as Template
                      </Button>
                      <Button variant="outline" onClick={() => handlePreview()}>
                        <Eye className="h-4 w-4 mr-2" />
                        Preview
                      </Button>
                      <Button variant="outline" onClick={handleSaveDraft}>Save Draft</Button>
                      <Button variant="outline" onClick={resetCompose} disabled={!subject.trim() && !editorContent.trim()}>
                        Clear
                      </Button>
                      {scheduleDate && (
                        <Button variant="outline" onClick={handleSchedule}>
                          <CalendarIcon className="h-4 w-4 mr-2" />
                          Schedule
                        </Button>
                      )}
                      <Button onClick={handleSendNow} disabled={sending}>
                        <Send className="h-4 w-4 mr-2" />
                        {sending ? "Sending..." : "Send Now"}
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

            </TabsContent>

            <TabsContent value="send-pdf" className="space-y-4">
              {/* Send as PDF Section */}
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle className="text-lg">Send as PDF</CardTitle>
                  <CardDescription>Send your newsletter as a downloadable PDF attachment instead.</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid gap-3 sm:grid-cols-2">
                    <button
                      type="button"
                      className={`relative h-auto py-6 px-4 flex flex-col items-center gap-3 rounded-lg border-2 transition-all duration-200 cursor-pointer ${
                        pdfMode === 'upload'
                          ? 'border-primary bg-primary/10 shadow-md'
                          : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50'
                      }`}
                      onClick={() => setPdfMode(pdfMode === 'upload' ? 'none' : 'upload')}
                    >
                      <div className={`rounded-full p-3 ${pdfMode === 'upload' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        <Upload className="h-6 w-6" />
                      </div>
                      <span className="font-semibold text-sm text-foreground">Upload PDF & Send</span>
                      <span className="text-xs text-muted-foreground text-center">Upload a PDF you created elsewhere</span>
                    </button>
                    <button
                      type="button"
                      className={`relative h-auto py-6 px-4 flex flex-col items-center gap-3 rounded-lg border-2 transition-all duration-200 ${
                        !subject.trim() || !editorContent.trim()
                          ? 'opacity-50 cursor-not-allowed border-border bg-muted'
                          : pdfMode === 'generate'
                            ? 'border-primary bg-primary/10 shadow-md cursor-pointer'
                            : 'border-border bg-card hover:border-primary/50 hover:bg-accent/50 cursor-pointer'
                      }`}
                      onClick={() => {
                        if (subject.trim() && editorContent.trim()) {
                          setPdfMode(pdfMode === 'generate' ? 'none' : 'generate');
                        }
                      }}
                      disabled={!subject.trim() || !editorContent.trim()}
                    >
                      <div className={`rounded-full p-3 ${pdfMode === 'generate' ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground'}`}>
                        <FileDown className="h-6 w-6" />
                      </div>
                      <span className="font-semibold text-sm text-foreground">Generate PDF & Send</span>
                      <span className="text-xs text-muted-foreground text-center">Convert your composed newsletter to PDF</span>
                    </button>
                  </div>

                  {pdfMode === 'upload' && (
                    <div className="space-y-3 border border-border rounded-lg p-4">
                      <div className="space-y-2">
                        <Label htmlFor="pdf-subject">Email Subject</Label>
                        <Input
                          id="pdf-subject"
                          placeholder="e.g. March Newsletter"
                          value={pdfSubject}
                          onChange={e => setPdfSubject(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="pdf-file">PDF File</Label>
                        <Input
                          id="pdf-file"
                          type="file"
                          accept=".pdf"
                          onChange={e => setPdfFile(e.target.files?.[0] || null)}
                        />
                      </div>
                      <Button
                        onClick={() => handleSendPdf('upload')}
                        disabled={sendingPdf || !pdfFile || !pdfSubject.trim()}
                        className="w-full"
                      >
                        <Send className="h-4 w-4 mr-2" />
                        {sendingPdf ? "Sending..." : "Upload & Send to Subscribers"}
                      </Button>
                    </div>
                  )}

                  {pdfMode === 'generate' && (
                    <div className="border border-border rounded-lg p-4 space-y-3">
                      <p className="text-sm text-muted-foreground">
                        This will convert your newsletter from the Compose tab, (subject: <strong>{subject || '—'}</strong>) into a PDF and email all subscribers a download link.
                      </p>
                      <Button
                        onClick={() => handleSendPdf('generate')}
                        disabled={sendingPdf || !subject.trim() || !editorContent.trim()}
                        className="w-full"
                      >
                        <FileDown className="h-4 w-4 mr-2" />
                        {sendingPdf ? "Generating & Sending..." : "Generate PDF & Send to Subscribers"}
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="templates" className="space-y-4">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Newsletter Templates</CardTitle>
                  <CardDescription>Use a ready-made template or save your own from the Compose tab</CardDescription>
                </CardHeader>
                <CardContent>
                  {templates.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <FileText className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No templates available</p>
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {/* Custom templates section */}
                      {templates.filter(t => !t.is_system_template).length > 0 && (
                        <div>
                          <h3 className="text-base font-semibold mb-3">My Templates</h3>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {templates.filter(t => !t.is_system_template).map(template => (
                              <div
                                key={template.id}
                                className="border border-border rounded-lg overflow-hidden flex flex-col"
                              >
                                {/* Accent bar */}
                                <div className="h-[3px] w-full bg-[hsl(210,79%,46%)]" />
                                
                                {/* Miniature preview thumbnail */}
                                <div className="px-2 pt-2">
                                  <div 
                                    className="relative overflow-hidden cursor-pointer border border-border rounded-md group bg-background"
                                    style={{ height: '160px' }}
                                    onClick={() => setTemplatePreview(template)}
                                  >
                                    <iframe
                                      srcDoc={`<html><head><style>body{margin:0;padding:0;overflow:hidden;transform-origin:top left;zoom:0.45;-moz-transform:scale(0.45);-moz-transform-origin:0 0;}</style></head><body>${template.is_system_template ? injectPtaLogo(template.html_content, ptaLogoUrl) : template.html_content}</body></html>`}
                                      className="border-0 pointer-events-none w-full"
                                      style={{ height: '800px', overflow: 'hidden' }}
                                      title={`Preview ${template.name}`}
                                      sandbox=""
                                      scrolling="no"
                                    />
                                    <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors flex items-center justify-center">
                                      <Eye className="h-6 w-6 text-foreground/0 group-hover:text-foreground/50 transition-colors" />
                                    </div>
                                  </div>
                                </div>

                                {/* Card body */}
                                <div className="p-3 flex flex-col gap-2 flex-1">
                                  <div className="flex items-start justify-between gap-2">
                                    <h3 className="font-medium text-sm truncate min-w-0">{template.name}</h3>
                                  </div>
                                  <div className="flex items-center gap-1 mt-auto">
                                    <Button size="sm" variant="outline" className="flex-1" onClick={() => handleUseTemplate(template)}>
                                      Use
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleEditTemplate(template)}>
                                      <Pencil className="h-4 w-4 text-foreground" />
                                    </Button>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(template.id)}>
                                      <Trash2 className="h-4 w-4 text-foreground" />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* System templates */}
                      <div>
                        {templates.filter(t => !t.is_system_template).length > 0 && (
                          <h3 className="text-base font-semibold mb-3">System Templates</h3>
                        )}
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {templates.filter(t => t.is_system_template).map(template => (
                            <div
                              key={template.id}
                              className="border border-border rounded-lg overflow-hidden flex flex-col"
                            >
                              {/* Accent bar */}
                              <div className="h-[3px] w-full bg-[hsl(24,95%,53%)]" />
                              
                              {/* Miniature preview thumbnail */}
                              <div className="px-2 pt-2">
                                <div 
                                  className="relative overflow-hidden cursor-pointer border border-border rounded-md group bg-background"
                                  style={{ height: '160px' }}
                                  onClick={() => setTemplatePreview(template)}
                                >
                                  <iframe
                                    srcDoc={`<html><head><style>body{margin:0;padding:0;overflow:hidden;transform-origin:top left;zoom:0.45;-moz-transform:scale(0.45);-moz-transform-origin:0 0;}</style></head><body>${template.is_system_template ? injectPtaLogo(template.html_content, ptaLogoUrl) : template.html_content}</body></html>`}
                                    className="border-0 pointer-events-none w-full"
                                    style={{ height: '800px', overflow: 'hidden' }}
                                    title={`Preview ${template.name}`}
                                    sandbox=""
                                    scrolling="no"
                                  />
                                  <div className="absolute inset-0 bg-foreground/0 group-hover:bg-foreground/5 transition-colors flex items-center justify-center">
                                    <Eye className="h-6 w-6 text-foreground/0 group-hover:text-foreground/50 transition-colors" />
                                  </div>
                                </div>
                              </div>

                              {/* Card body */}
                              <div className="p-3 flex flex-col gap-2 flex-1">
                                <div className="flex items-start justify-between gap-2">
                                  <h3 className="font-medium text-sm truncate min-w-0">{template.name}</h3>
                                  <Badge variant="secondary" className="shrink-0 text-xs">System</Badge>
                                </div>
                                <div className="flex items-center gap-1 mt-auto">
                                  <Button size="sm" variant="outline" className="flex-1" onClick={() => handleUseTemplate(template)}>
                                    Use
                                  </Button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="drafts" className="space-y-4">
              <Card className="shadow-sm">
                <CardHeader>
                  <CardTitle>Drafts & Scheduled</CardTitle>
                </CardHeader>
                <CardContent>
                  {draftNewsletters.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Mail className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No drafts or scheduled newsletters</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Subject</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Scheduled</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {draftNewsletters.map(nl => (
                          <TableRow key={nl.id}>
                            <TableCell className="font-medium">{nl.subject}</TableCell>
                            <TableCell>
                              <Badge variant={nl.status === 'scheduled' ? 'default' : 'secondary'}>
                                {nl.status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              {nl.scheduled_at ? format(new Date(nl.scheduled_at), 'PPP HH:mm') : '—'}
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="sm" onClick={() => handleEdit(nl)}>
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => handlePreview(nl)}>
                                  <Eye className="h-4 w-4" />
                                </Button>
                                {nl.status === 'scheduled' && (
                                  <Button variant="ghost" size="sm" onClick={() => handleCancelSchedule(nl.id)}>
                                    Cancel
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" onClick={() => handleDelete(nl.id)} className="text-destructive">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="sent" className="space-y-4">
              {sentNewsletters.length === 0 ? (
                <Card className="shadow-sm">
                  <CardContent className="py-8">
                    <div className="text-center text-muted-foreground">
                      <Send className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No newsletters sent yet</p>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4">
                  {sentNewsletters.map(nl => {
                    const recipientCount = nl.recipient_count || 0;
                    const openCount = nl.open_count || 0;
                    const unreadCount = Math.max(recipientCount - openCount, 0);
                    return (
                      <Card key={nl.id} className="shadow-sm">
                        <CardContent className="p-5">
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="font-semibold text-base truncate">{nl.subject}</h3>
                              <p className="text-sm text-muted-foreground mt-1">
                                Sent {nl.sent_at ? format(new Date(nl.sent_at), 'PPP') : '—'} · {recipientCount} recipient{recipientCount !== 1 ? 's' : ''}
                              </p>
                              <div className="flex items-center gap-4 mt-3">
                                <div className="flex items-center gap-1.5">
                                  <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                                  <span className="text-sm font-medium">{openCount} Read</span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <div className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                                  <span className="text-sm font-medium">{unreadCount} Unread</span>
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center gap-1">
                              <Button variant="ghost" size="sm" onClick={() => handlePreview(nl)}>
                                <Eye className="h-4 w-4 text-foreground" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDelete(nl.id)}>
                                <Trash2 className="h-4 w-4 text-foreground" />
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </TabsContent>

            <TabsContent value="subscribers" className="space-y-4">
              <Card className="shadow-sm">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle>Subscribers</CardTitle>
                      <CardDescription>{activeSubscribers.length} active, {subscribers.length - activeSubscribers.length} unsubscribed</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="mb-4">
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                      <Input
                        placeholder="Search by email..."
                        value={subscriberSearch}
                        onChange={e => setSubscriberSearch(e.target.value)}
                        className="pl-9"
                      />
                    </div>
                  </div>
                  {filteredSubscribers.length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <Users className="h-12 w-12 mx-auto mb-3 opacity-40" />
                      <p>No subscribers found</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Email</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Joined</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredSubscribers.map(sub => (
                          <TableRow key={sub.id}>
                            <TableCell>{sub.email}</TableCell>
                            <TableCell>
                              <Badge variant={sub.is_subscribed ? 'default' : 'secondary'}>
                                {sub.is_subscribed ? 'Active' : 'Unsubscribed'}
                              </Badge>
                            </TableCell>
                            <TableCell>{format(new Date(sub.created_at), 'PPP')}</TableCell>
                            <TableCell>
                              <Button variant="ghost" size="sm" onClick={() => handleRemoveSubscriber(sub.id)}>
                                <Trash2 className="h-4 w-4 text-foreground" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="social-media" className="space-y-4">
          <Card className="shadow-sm">
            <CardHeader>
              <CardTitle>Facebook Integration</CardTitle>
              <CardDescription>Connect and post to your PTA's Facebook page</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div className="flex items-center gap-3">
                  <Facebook className="h-8 w-8 text-blue-600" />
                  <div>
                    <p className="font-semibold">Facebook Page</p>
                    <p className="text-sm text-muted-foreground">Connect your PTA Facebook page</p>
                  </div>
                </div>
                <Switch />
              </div>

              <div className="p-4 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-800 dark:text-blue-200">
                  Facebook integration must be enabled by a PTA Admin before you can post from this dashboard.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Preview Dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Preview: {previewSubject}</DialogTitle>
            <DialogDescription>This is how your newsletter will appear to subscribers</DialogDescription>
          </DialogHeader>
          <div className="border border-border rounded-lg bg-white overflow-hidden">
            <iframe
              srcDoc={previewContent}
              className="w-full border-0"
              style={{ minHeight: '500px', height: '70vh' }}
              title="Newsletter Preview"
            />
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Create/Edit Dialog */}
      <Dialog open={templateDialogOpen} onOpenChange={setTemplateDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editTemplateId ? 'Edit Template' : 'Save as Template'}</DialogTitle>
            <DialogDescription>
              {editTemplateId ? 'Update your template details' : 'Save the current content as a reusable template'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="template-name">Template Name</Label>
              <Input
                id="template-name"
                placeholder="e.g. Monthly Update"
                value={templateName}
                onChange={e => setTemplateName(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="template-subject">Subject Line</Label>
              <Input
                id="template-subject"
                placeholder="e.g. Our Monthly Newsletter"
                value={templateSubject}
                onChange={e => setTemplateSubject(e.target.value)}
              />
            </div>
            {editTemplateId && (
              <div className="space-y-2">
                <Label>Content</Label>
                <Editor
                  value={templateContent}
                  onEditorChange={(content) => setTemplateContent(content)}
                  licenseKey="gpl"
                  init={{
                    height: 250,
                    menubar: false,
                    plugins: 'advlist autolink lists link image table code',
                    toolbar: 'undo redo | blocks | bold italic underline | bullist numlist | link image | code | removeformat',
                    skin: false,
                    content_css: false,
                    branding: false,
                    promotion: false,
                    content_style: 'body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif; font-size: 14px; padding: 8px; }',
                  }}
                />
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setTemplateDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveTemplate}>
                {editTemplateId ? 'Update Template' : 'Save Template'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Template Preview Dialog */}
      <Dialog open={!!templatePreview} onOpenChange={(open) => !open && setTemplatePreview(null)}>
        {templatePreview && (
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                {templatePreview.name}
              </DialogTitle>
              <DialogDescription>{templatePreview.subject}</DialogDescription>
            </DialogHeader>
            <div className="border rounded-lg bg-background">
              <iframe
                srcDoc={templatePreview.is_system_template ? injectPtaLogo(templatePreview.html_content, ptaLogoUrl) : templatePreview.html_content}
                className="w-full min-h-[500px] border-0 rounded-lg"
                title="Template Preview"
                sandbox="allow-same-origin"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setTemplatePreview(null)}>Close</Button>
              <Button onClick={() => { handleUseTemplate(templatePreview); setTemplatePreview(null); }}>
                Use Template
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </div>
  );
}
